<template>
  <v-card outlined>
    <v-card-text>
      <div class="overline">
        <h2>{{ $t("component.resultsPanel.targetData") }}</h2>
      </div>

      <v-row>
        <!-- Remarks -->
        <v-col cols="6">
          <div class="overline text-weight-bold">Remarks</div>
          <span v-if="entry.remarks">
            {{ entry.remarks.replace(/[()]/g, "") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
        <v-col cols="4">
          <div class="overline text-weight-bold">
            Programs & Additional Sanctions
          </div>
          <span
            v-if="
              (entry.programs && entry.programs.length > 0) ||
              (entry.additionalSanctions &&
                entry.additionalSanctions.length > 0)
            "
          >
            {{ entry.programs.join(", ") }}
            <br />
            {{ entry.additionalSanctions.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
      </v-row>

      <!-- DOBs, Gender, Citizenship, Birthplace -->
      <v-row>
        <v-col cols="2">
          <div class="overline text-weight-bold">Birthplace</div>
          <span v-if="entry.placesOfBirth && entry.placesOfBirth.length > 0">
            {{
              entry.placesOfBirth.map((item) => item["placeOfBirth"]).join(", ")
            }}
          </span>
          <span v-else>N/A</span>
        </v-col>
        <v-col cols="2">
          <div class="overline text-weight-bold">Citizenship</div>
          <span v-if="entry.citizenship && entry.citizenship.length > 0">
            {{ entry.citizenship.join(", ").toUpperCase() }}
          </span>
          <span v-else>N/A</span>
        </v-col>
        <v-col cols="2">
          <div class="overline text-weight-bold">Nationality</div>
          <span v-if="entry.nationality && entry.nationality.length > 0">
            {{ entry.nationality.map((item) => item["country"]).join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
        <v-col cols="2">
          <div class="overline text-weight-bold">Date of Birth</div>
          <span v-if="entry.dobs && entry.dobs.length > 0">
            {{ entry.dobs.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="2">
          <div class="overline text-weight-bold">Gender</div>
          <span v-if="entry.gender && entry.gender.length > 0">
            {{ entry.gender }}
          </span>
          <span v-else>N/A</span>
        </v-col>
      </v-row>

      <!-- Phone, Email -->
      <v-row>
        <v-col>
          <div class="overline text-weight-bold">
            <v-icon small>mdi-phone</v-icon> Phone Numbers
          </div>
          <span v-if="entry.phoneNumbers && entry.phoneNumbers.length > 0">
            {{ entry.phoneNumbers.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>

        <v-col>
          <div class="overline text-weight-bold">
            <v-icon small>mdi-email</v-icon> Email
          </div>
          <span v-if="entry.emailAddresses && entry.emailAddresses.length > 0">
            {{ entry.emailAddresses.join(", ") }}
          </span>
          <span v-else>N/A</span>
        </v-col>
      </v-row>

      <!-- Alias -->
      <div class="overline">Aliases</div>
      <v-row dense>
        <v-col cols="12" v-if="entry.akas && entry.akas.length > 0">
          <v-chip
            dark
            small
            v-for="(aka, index) in entry.akas"
            :key="index"
            class="mr-1 mb-1"
            :color="
              aka.category == 'strong' ? 'red lighten-2' : 'grey lighten-1'
            "
          >
            {{ getAkaName(aka) }}
          </v-chip>
        </v-col>
        <v-col v-else> N/A </v-col>
      </v-row>

      <!-- if pep -->
      <v-row dense v-if="entry.positions.length > 0">
        <v-col cols="12">
          <div class="overline">
            {{ $t("component.resultsPanel.positions") }}
          </div>
          <v-chip
            small
            v-for="(position, index) in entry.positions"
            :key="index"
            class="mr-1 mb-1"
          >
            {{ position }}
          </v-chip>
        </v-col>
      </v-row>

      <!-- Education -->
      <v-row dense>
        <v-col cols="6" v-if="entry.education.length > 0">
          <div class="overline">
            {{ $t("component.resultsPanel.education") }}
          </div>
          <ul>
            <li v-for="(education, index) in entry.education" :key="index">
              {{ education }}
            </li>
          </ul>
        </v-col>

        <v-col cols="6" v-if="entry.websites.length > 0">
          <div class="overline">
            {{ $t("component.resultsPanel.websites") }}
          </div>
          <ul>
            <li v-for="(website, index) in entry.websites" :key="index">
              {{ website }}
            </li>
          </ul>
        </v-col>
      </v-row>

      <!-- Addresses -->
      <div class="overline">{{ $t("component.resultsPanel.addresses") }}</div>
      <v-row dense>
        <template v-if="entry.addresses && entry.addresses.length > 0">
          <template v-for="address in entry.addresses">
            <v-col cols="3" :key="'col-' + address.uid">
              <Address
                :key="address.uid"
                :address1="address.address1"
                :address2="address.address2"
                :city="address.city"
                :state="address.stateOrProvince"
                :postalCode="address.postalCode"
                :country="address.country"
              />
            </v-col>
          </template>
          <v-spacer />
        </template>
        <template v-else>
          <v-col> N/A </v-col>
        </template>
      </v-row>

      <!-- IDs -->
      <div class="overline">Passports & Other IDs</div>
      <v-row dense>
        <template v-if="entry.passports && entry.passports.length > 0">
          <!-- Passports -->
          <template v-for="passport in entry.passports">
            <v-col cols="2" :key="'col-' + passport.uid">
              <ID
                :key="passport.uid"
                type="Passport"
                :country="passport.passportCountry"
                :number="passport.passport"
                icon="fa-id-card"
                :expiration="passport.expirationDate"
                :issued="passport.issueDate"
              />
            </v-col>
          </template>
        </template>
        <template v-if="entry.ids && entry.ids.length > 0">
          <!-- TO DO add expiration/issued fields to ID backend and populate here -->
          <template v-for="id in entry.ids">
            <v-col cols="2" :key="'col-' + id.id">
              <ID
                :key="id.id"
                :type="id.type"
                :country="id.country"
                :number="id.number"
                icon="fa-id-card"
              />
            </v-col>
          </template>
        </template>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import Address from "../Address";
import ID from "../../components/ID.vue";
export default {
  name: "TargetData",
  components: {
    Address,
    ID,
  },
  data() {
    return {};
  },
  props: {
    entry: {},
  },
  methods: {
    getAkaName(aka) {
      var name = "";
      if (aka.firstName && aka.lastName) {
        name = aka.firstName + " " + aka.lastName;
      } else if (aka.firstName) {
        name = aka.firstName;
      } else if (aka.lastName) {
        name = aka.lastName;
      }
      return name;
    },
  },
};
</script>